import { api } from '../api/vykoClient';
import { HandoverNote } from '../types';

export interface HandoverAssignmentRecord {
  hospitalNumber: string | number;
  ipNumber: string | number;
  targetNurseLoginId: string;
  targetNurseName: string;
  targetNurseEmpId?: string | number;
  assignedByLoginId: string;
  assignedByName?: string;
  assignedAt: number;
  reportKey?: string;
  verified?: boolean;
  ackStatus?: string; // 'NTC0001' (Pending) | 'NTC0002' (Accepted)
  verifiedAt?: number;
}

const STORAGE_KEY = 'vyko_assigned_handovers_v1';
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours expiry for shift tracking

class HandoverTrackingService {
  private cache: Map<string, HandoverAssignmentRecord> = new Map();
  private isLoaded = false;

  constructor() {
    this.loadFromStorage();
  }

  private getKey(hospitalNumber: string | number, ipNumber: string | number): string {
    return `${hospitalNumber}-${ipNumber}`;
  }

  private loadFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, HandoverAssignmentRecord>;
        const now = Date.now();
        let changed = false;

        for (const [k, rec] of Object.entries(parsed)) {
          if (rec && rec.assignedAt && now - rec.assignedAt < EXPIRY_MS) {
            this.cache.set(k, rec);
          } else {
            changed = true;
          }
        }

        if (changed) {
          this.saveToStorage();
        }
      }
    } catch (err) {
      console.warn('Failed loading assigned handovers from storage:', err);
    } finally {
      this.isLoaded = true;
    }
  }

  private saveToStorage() {
    try {
      const obj: Record<string, HandoverAssignmentRecord> = {};
      for (const [k, v] of this.cache.entries()) {
        obj[k] = v;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (err) {
      console.warn('Failed saving assigned handovers to storage:', err);
    }
  }

  /**
   * Optimistically record an assignment immediately after giveHandover returns success
   */
  public recordAssignment(record: HandoverAssignmentRecord): void {
    const key = this.getKey(record.hospitalNumber, record.ipNumber);
    this.cache.set(key, {
      ...record,
      assignedAt: record.assignedAt || Date.now(),
    });
    this.saveToStorage();
  }

  /**
   * Retrieve cached assignment for a patient
   */
  public getAssignment(hospitalNumber: string | number, ipNumber: string | number): HandoverAssignmentRecord | null {
    if (!this.isLoaded) {
      this.loadFromStorage();
    }
    const key = this.getKey(hospitalNumber, ipNumber);
    const rec = this.cache.get(key);
    if (!rec) return null;

    // Prune if expired
    if (Date.now() - rec.assignedAt > EXPIRY_MS) {
      this.cache.delete(key);
      this.saveToStorage();
      return null;
    }

    return rec;
  }

  /**
   * Get all active assigned handovers
   */
  public getAllAssignments(): Map<string, HandoverAssignmentRecord> {
    if (!this.isLoaded) {
      this.loadFromStorage();
    }
    return new Map(this.cache);
  }

  /**
   * Pull the last note for a patient, check if it is a handover note,
   * determine who it was given to, and update tracking state.
   */
  public async checkPatientLastNote(
    hospitalNumber: string | number,
    ipNumber: string | number
  ): Promise<{ isHandover: boolean; record: HandoverAssignmentRecord | null; lastNote: HandoverNote | null }> {
    if (!this.isLoaded) {
      this.loadFromStorage();
    }
    const key = this.getKey(hospitalNumber, ipNumber);
    try {
      const notes: HandoverNote[] = await api.fetchHandover(String(hospitalNumber), String(ipNumber));
      if (!Array.isArray(notes) || notes.length === 0) {
        this.cache.delete(key);
        this.saveToStorage();
        return { isHandover: false, record: null, lastNote: null };
      }

      // Sort notes so the true last/latest note is index 0
      const sorted = [...notes].sort((a, b) => {
        const tA = a.updatedAt ? new Date(String(a.updatedAt)).getTime() : 0;
        const tB = b.updatedAt ? new Date(String(b.updatedAt)).getTime() : 0;
        if (tA && tB && tA !== tB) return tB - tA;
        const idA = Number(a.noteId) || 0;
        const idB = Number(b.noteId) || 0;
        return idB - idA;
      });

      const lastNote = sorted[0];
      const isHandover = Boolean(
        lastNote &&
        (lastNote.noteCode === 'TPTY0026' ||
          (typeof lastNote.noteCodeName === 'string' &&
            lastNote.noteCodeName.toUpperCase().includes('HANDOVER')))
      );

      if (isHandover && lastNote) {
        const isAck =
          String(lastNote.ackStatus).toUpperCase() === 'ACKNOWLEDGED' ||
          String(lastNote.ackStatus).toUpperCase() === 'NTC0002';

        const existing = this.cache.get(key);
        const targetNurseName =
          (lastNote.handeOverAssignedUserName as string) ||
          (existing?.targetNurseName) ||
          (lastNote.handoverTo ? String(lastNote.handoverTo) : 'Nurse');

        const record: HandoverAssignmentRecord = {
          hospitalNumber,
          ipNumber,
          targetNurseLoginId: String(lastNote.handoverTo || ''),
          targetNurseName,
          assignedByLoginId: String(lastNote.userId || lastNote.loginId || existing?.assignedByLoginId || ''),
          assignedByName: String(lastNote.doctorName || existing?.assignedByName || ''),
          assignedAt: lastNote.updatedAt ? new Date(String(lastNote.updatedAt)).getTime() : (existing?.assignedAt || Date.now()),
          reportKey: lastNote.reportKey ? String(lastNote.reportKey) : existing?.reportKey,
          verified: true,
          ackStatus: isAck ? 'NTC0002' : 'NTC0001',
          verifiedAt: Date.now(),
        };

        this.cache.set(key, record);
        this.saveToStorage();
        return { isHandover: true, record, lastNote };
      } else {
        // Last note is not a handover note (e.g. progress record or discharge)
        this.cache.delete(key);
        this.saveToStorage();
        return { isHandover: false, record: null, lastNote };
      }
    } catch (err) {
      console.warn(`Could not check last note for patient ${hospitalNumber}/${ipNumber}:`, err);
      const existing = this.getAssignment(hospitalNumber, ipNumber);
      return { isHandover: !!existing, record: existing, lastNote: null };
    }
  }

  /**
   * Verify handover assignment against backend route: doctor-desk-mobile/doctor-visit-details
   */
  public async verifyAssignment(
    hospitalNumber: string | number,
    ipNumber: string | number
  ): Promise<HandoverAssignmentRecord | null> {
    const res = await this.checkPatientLastNote(hospitalNumber, ipNumber);
    return res.record;
  }

  /**
   * Clear an assignment record (e.g. if revoked or reassigned)
   */
  public clearAssignment(hospitalNumber: string | number, ipNumber: string | number): void {
    const key = this.getKey(hospitalNumber, ipNumber);
    if (this.cache.has(key)) {
      this.cache.delete(key);
      this.saveToStorage();
    }
  }
}

export const handoverTrackingService = new HandoverTrackingService();
